import { IconName } from 'components/Icon'
import { t } from 'lib/i18n'

export interface DayForecast {
  date: string
  weatherCode: number
  tempMax: number
  tempMin: number
  precipitationProbability: number
  windSpeedMax: number
}

interface OpenMeteoResponse {
  daily: {
    time: string[]
    weathercode: number[]
    temperature_2m_max: number[]
    temperature_2m_min: number[]
    precipitation_probability_max: number[]
    windspeed_10m_max: number[]
  }
}

/** Open-Meteo: free, keyless, CORS-enabled forecast API — no backend proxy needed. */
export const fetchWeather = async (latitude: number, longitude: number): Promise<DayForecast[]> => {
  const params = new URLSearchParams({
    daily: 'weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max',
    forecast_days: '7',
    latitude: String(latitude),
    longitude: String(longitude),
    timezone: 'Asia/Amman',
  })
  const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
  if (!response.ok) throw new Error(t('تعذر تحميل توقعات الطقس', 'Could not load the weather forecast'))
  const data: OpenMeteoResponse = await response.json()
  return data.daily.time.map((date, index) => ({
    date,
    precipitationProbability: data.daily.precipitation_probability_max[index],
    tempMax: Math.round(data.daily.temperature_2m_max[index]),
    tempMin: Math.round(data.daily.temperature_2m_min[index]),
    weatherCode: data.daily.weathercode[index],
    windSpeedMax: Math.round(data.daily.windspeed_10m_max[index]),
  }))
}

/** WMO weather codes (Open-Meteo's `weathercode`) mapped to the closest icon we have. */
export const weatherIcon = (code: number): IconName => {
  if (code === 0) return 'sun'
  if (code >= 71 && code <= 77) return 'cloudSnow'
  if (code >= 95) return 'cloudLightning'
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'cloudRain'
  return 'cloud'
}

const WIND_LIMIT_KMH = 40
const RAIN_PROBABILITY_LIMIT = 50

/** Simple, honest construction-safety thresholds applied to real forecast data — not a fabricated alert. */
export const workAlert = (day: DayForecast): string | null => {
  if (day.windSpeedMax > WIND_LIMIT_KMH) return t('رياح قوية — لا رفع بالرافعة', 'Strong wind — no crane lifts')
  if (day.precipitationProbability >= RAIN_PROBABILITY_LIMIT) return t('احتمال أمطار — لا صب خرسانة', 'Rain likely — no concrete pours')
  return null
}
